/**
 * RealTimeFeedback - Real-time feedback updates display with clickable cards
 */
import React, { useState, useCallback } from 'react';
import { useRealTimeFeedback } from '../../hooks/useRealTimeFeedback';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useApiService } from '../../services/apiService';
import FeedbackCard from '../molecules/FeedbackCard';
import FeedbackDetails from '../organisms/FeedbackDetails';
import FeedbackStatus from '../atoms/FeedbackStatus';
import Modal from '../atoms/Modal';
import { Button } from '../atoms/Button';
import { formatDistanceToNow } from 'date-fns';
import { FeedbackStatusType } from '../atoms/FeedbackStatus';
import { FeedbackPriorityType } from '../molecules/FeedbackCard';

// Types for feedback data
interface FeedbackData {
  id: number;
  title: string;
  description: string;
  status: FeedbackStatusType;
  priority: FeedbackPriorityType;
  created_at: string;
  updated_at?: string;
  user?: any;
  app?: any;
  assigned_to?: string;
  assigned_user?: any;
  external_id?: string;
  source_url?: string;
  extra_data?: any;
  sentiment_analysis?: {
    sentiment: 'positive' | 'negative' | 'neutral';
    confidence: number;
    themes: string[];
    summary: string;
    recommendations: string[];
    analyzed_at: string;
    model_version: string;
    context_analysis?: string;
  };
}

interface RealTimeFeedbackProps {
  maxDisplayItems?: number;
  showConnectionStatus?: boolean;
  className?: string;
}

const RealTimeFeedback: React.FC<RealTimeFeedbackProps> = ({
  maxDisplayItems = 10,
  showConnectionStatus = true,
  className = ''
}) => {
  const { isConnected, connectionState, sendPing } = useWebSocket();
  const {
    newFeedback,
    statusUpdates,
    comments,
    lastUpdate,
    loading,
    clearAll,
    getFeedbackCount,
    getStatusUpdateCount,
    getCommentCount
  } = useRealTimeFeedback();
  
  const apiService = useApiService();

  const [activeTab, setActiveTab] = useState<'feedback' | 'status' | 'comments'>('feedback');
  
  // Modal state for detailed feedback view
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Handle feedback card click - open detailed modal
  const handleFeedbackClick = async (feedback: FeedbackData) => {
    setSelectedFeedback(feedback);
    setIsModalOpen(true);
    
    // Always fetch complete data including comments for modal display
    if (apiService) {
      setLoadingDetail(true);
      try {
        const response = await apiService.getFeedbackById(feedback.id, {
          include_comments: true,
          include_user: true,
          include_app: true
        });
        setSelectedFeedback(response.data as FeedbackData);
      } catch (err) {
        console.error('Error fetching detailed feedback:', err);
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedFeedback(null);
    setLoadingDetail(false);
  };

  // Handle status/priority changes from detail modal
  const handleStatusChange = async (id: number, status: FeedbackStatusType) => {
    if (!apiService) return;
    
    try {
      await apiService.updateFeedback(id, { status });
      
      // Update selected feedback if it's the same one
      if (selectedFeedback?.id === id) {
        setSelectedFeedback(prev => prev ? { ...prev, status } : null);
      }
    } catch (err) {
      console.error('Error updating feedback status:', err);
    }
  };

  const handlePriorityChange = async (id: number, priority: FeedbackPriorityType) => {
    if (!apiService) return;
    
    try {
      await apiService.updateFeedback(id, { priority });
      
      // Update selected feedback if it's the same one
      if (selectedFeedback?.id === id) {
        setSelectedFeedback(prev => prev ? { ...prev, priority } : null);
      }
    } catch (err) {
      console.error('Error updating feedback priority:', err);
    }
  };

  const handleAddComment = useCallback(async (feedbackId: number, content: string) => {
    if (!apiService) return;
    
    try {
      const response = await apiService.createComment({
        feedback_id: feedbackId,
        content,
        is_internal: false
      });
      
      console.log('Comment created successfully:', response.data);
      
      // The WebSocket integration will automatically refresh the UI
      // No need to manually refresh here as the real-time comment updates will handle it
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  }, [apiService]);

  const getConnectionStatusColor = () => {
    switch (connectionState) {
      case 'connected':
        return 'text-green-600 bg-green-100 border-green-200';
      case 'connecting':
        return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'error':
        return 'text-red-600 bg-red-100 border-red-200';
      default:
        return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getConnectionStatusIcon = () => {
    switch (connectionState) {
      case 'connected':
        return '🟢';
      case 'connecting':
        return '🟡';
      case 'error':
        return '🔴';
      default:
        return '⚪';
    }
  };

  const formatFeedbackForCard = (feedback: any) => {
    return {
      ...feedback,
      id: parseInt(feedback.id) || 0,
      user: feedback.user ? {
        auth0_user_id: feedback.user.auth0_user_id,
        name: feedback.user.name,
        email: feedback.user.email,
        picture: feedback.user.picture
      } : undefined,
      app: feedback.app ? {
        client_id: feedback.app.client_id,
        name: feedback.app.name,
        description: feedback.app.description,
        logo_uri: feedback.app.logo_uri
      } : undefined
    };
  };

  const renderFeedbackList = () => {
    if (loading) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p>Loading recent feedback...</p>
        </div>
      );
    }

    const recentFeedback = newFeedback.slice(0, maxDisplayItems);
    
    // Debug logging
    console.log('Total feedback items:', newFeedback.length);
    console.log('Recent feedback to display:', recentFeedback.length);
    console.log('Max display items:', maxDisplayItems);
    
    if (recentFeedback.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No recent feedback updates</p>
          <p className="text-sm mt-1">New feedback will appear here in real-time</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {recentFeedback.map((feedback) => {
          const formattedFeedback = formatFeedbackForCard(feedback);
          console.log('Rendering feedback:', feedback.id, formattedFeedback);
          return (
            <div key={feedback.id} className="animate-fadeIn">
              <FeedbackCard
                feedback={formattedFeedback}
                onClick={handleFeedbackClick}
                compact={true}
                className="border-l-4 border-blue-500 hover:shadow-md transition-shadow cursor-pointer"
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderStatusUpdates = () => {
    const recentUpdates = statusUpdates.slice(0, maxDisplayItems);
    
    if (recentUpdates.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No recent status updates</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {recentUpdates.map((update, index) => (
          <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 animate-fadeIn">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">Feedback #{update.feedback_id}</span>
                <FeedbackStatus status={update.status as any} size="sm" />
              </div>
              {update.updated_by && (
                <span className="text-sm text-gray-500">by {update.updated_by}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderComments = () => {
    const recentComments = comments.slice(0, maxDisplayItems);
    
    if (recentComments.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No recent comments</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {recentComments.map((comment, index) => (
          <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 animate-fadeIn">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Feedback #{comment.feedback_id}</span>
              <span className="text-xs text-gray-500">New comment</span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {comment.comment?.content || 'Comment content not available'}
            </p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Real-Time Updates
            </h3>
            {showConnectionStatus && (
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getConnectionStatusColor()}`}>
                {getConnectionStatusIcon()} {connectionState}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={sendPing}
              variant="outline"
              size="sm"
              disabled={!isConnected}
            >
              Ping
            </Button>
            <Button
              onClick={clearAll}
              variant="outline"
              size="sm"
            >
              Clear All
            </Button>
          </div>
        </div>
        
        {lastUpdate && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Last update: {formatDistanceToNow(lastUpdate, { addSuffix: true })}
          </p>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8 px-4" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('feedback')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'feedback'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Feedback ({Math.min(getFeedbackCount(), maxDisplayItems)}/{getFeedbackCount()})
          </button>
          <button
            onClick={() => setActiveTab('status')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'status'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Status ({getStatusUpdateCount()})
          </button>
          <button
            onClick={() => setActiveTab('comments')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'comments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Comments ({getCommentCount()})
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="p-4 max-h-96 overflow-y-auto">
        {activeTab === 'feedback' && renderFeedbackList()}
        {activeTab === 'status' && renderStatusUpdates()}
        {activeTab === 'comments' && renderComments()}
      </div>

      {/* Detailed Feedback Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        size="xl"
        showCloseButton={false}
        closeOnBackdropClick={true}
        closeOnEscape={true}
      >
        {selectedFeedback ? (
          <div className="p-6">
            {loadingDetail ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">
                  Loading detailed information...
                </span>
              </div>
            ) : (
              <FeedbackDetails
                feedback={selectedFeedback}
                onStatusChange={handleStatusChange}
                onPriorityChange={handlePriorityChange}
                onAddComment={handleAddComment}
                onClose={handleModalClose}
                className="border-0 shadow-none"
              />
            )}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            No feedback selected
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RealTimeFeedback;